// Third party components
import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import {Divider, Grid, Typography, Card, CardContent, Container, Skeleton} from '@mui/material';

// Shared components
import {LineLoader} from '@timmons-group/shared-react-components';

// Hooks
import ThemeMap from 'components/ThemeMap';
import { useMapConfig } from '@timmons-group/shared-react-components/hooks';

import { dateFormatter } from '@timmons-group/shared-react-components/helpers';
import { transformExtent } from 'ol/proj';

const CommunityMap = ({ data, config }) => {
  const olMap = useRef(null);
  const oThemeMap = useRef(null);
  const themeMapInstance = useRef(null);
  const [features, setFeatures] = useState([]);
  const [mapReady, setMapReady] = useState(false);
  const [extent, setExtent] = useState(null);

  const setMap = (olm) => {
    oThemeMap.current = olm;
    // olMap is a ref to the map. So we need to get the .current property
    olMap.current = olm.olMap.current;
    setMapReady(true);
  };

  useEffect(() => {
    if (mapReady) {
      if(data && data.geometry)
        setFeatures([data.geometry]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]);

  useEffect(() => {
    if (mapReady && features.length > 0 && themeMapInstance.current) {
      const updatedExt = transformExtent(themeMapInstance.current?.getFeatureLayerExtent(), 'EPSG:3857','EPSG:4326');
      setExtent(updatedExt);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [features]);

  if (!config) {
    return <p>No map config found</p>;
  }

  return (
    <ThemeMap
      ref={themeMapInstance}
      existingFeatureLayer="community:details:community"
      defaultExtent={extent}
      themes={config}
      allowFeatures={true}
      features={features}
      mapSet={setMap}
    />
  );
}

CommunityMap.propTypes = {
  data: PropTypes.object.isRequired,
  config: PropTypes.object,
}

const CommunityOverview = ({community, loading}) => {
  const [mapConfig, mapConfigLoading] = useMapConfig('community_detail');

  const renderDetails = () => {
    if (loading) {
      return <>
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton />
        <Skeleton />
      </>
    }

    return (
      <>
        <p>
          <b>Name: </b>
          {community?.name}
        </p>
        <p>
          <b>Created Date: </b>{dateFormatter(community?.createdOn)}
        </p>
        <p>
          <b>Created By: </b>{community?.createdBy?.name || community?.createdBy?.email}
        </p>
        <p>
          <b>Community Type: </b>
          {community?.communityType?.name}
        </p>
        <p>
          <b>State: </b>
          {community?.state?.name}
        </p>
      </>
    );
  }

  return (
    <Container maxWidth={false} sx={{marginTop: 2}}>
      <Card>
        <CardContent>
          <Typography variant="sectionHeader">Summary</Typography>
        </CardContent>
        <Divider/>
        <CardContent>
          <Grid container spacing={0}>
            <Grid item xs={12} md={6}>
              {mapConfigLoading || loading || !community ?
                <LineLoader message="Loading Map..." color="secondary" />
                :
                <CommunityMap data={community} config={mapConfig}/>
              }
            </Grid>
            <Grid item xs={12} md={6} sx={{paddingLeft: 3, paddingRight: 3}}>
              <Typography variant="sectionHeader">Details</Typography>
              <Divider />
              {renderDetails()}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

CommunityOverview.propTypes = {
  community: PropTypes.object,
  loading: PropTypes.bool,
}

export default CommunityOverview;
